import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { moderationApi, postApi } from '../../services/api'
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  Field,
  Fieldset,
  StatePanel,
  Textarea,
} from '../common'
import { checkFailureWords, moderationStatusView } from './moderationReviewModel'
import { checksWords } from './moderationWords'
import { uniqueFindingLabels } from './moderationFindings'
import {
  OUTAGE_CHECKS,
  UNRESOLVED_POLICY_MESSAGE,
  canSubmitOffRelease,
  canSubmitOutageRelease,
  classifyOverrideError,
  defaultOutageChecks,
  exemptionView,
  moderationStateLabel,
  noOverrideExplanation,
  nonOutageFailureNotice,
  notCheckedLabel,
  offReleaseDescription,
  offReleasePayload,
  outageChecksHelp,
  outageReleaseNotice,
  outageReleasePayload,
  overrideDialogDescription,
  overrideOptions,
  overrideSuccessMessage,
  releasedByLabel,
  revokeSuccessMessage,
  toggleCheck,
  versionsKnown,
} from './moderationOverrideModel'
import styles from './ModerationOverrideDialog.module.css'

const TONE_VARIANTS = { success: 'success', warning: 'warning', error: 'error', info: 'info', default: 'info' }

// The query the composer's status panel reads too, so a release here also
// refreshes an open editor.
function postModerationQueryKey(orgSlug, postId) {
  return ['post-moderation', orgSlug, postId]
}

function ExemptionItem({ exemption, open, reason, error, pending, disabled, onOpen, onCancel, onReasonChange, onRevoke }) {
  const view = exemptionView(exemption)
  const reasonId = `revoke-reason-${exemption.id}`
  return (
    <li className={styles.exemption}>
      <div className={styles.exemptionHeader}>
        <div className={styles.exemptionTitle}>
          <strong>{view.type}</strong>
          <span>{view.subject}</span>
        </div>
        {!open ? (
          <Button
            size="compact"
            variant="secondary"
            disabled={disabled}
            aria-label={`Revoke ${view.type.toLowerCase()} on ${view.subject.toLowerCase()}`}
            onClick={onOpen}
          >
            Revoke
          </Button>
        ) : null}
      </div>
      <dl className={styles.details}>
        <div><dt>Checks covered</dt><dd>{view.checks}</dd></div>
        {view.grantedBy ? <div><dt>Granted by</dt><dd>{view.grantedBy}</dd></div> : null}
        {view.stale ? <div><dt>Counts</dt><dd>No longer counts under the current policy</dd></div> : null}
        {view.reason ? <div><dt>Reason</dt><dd>{view.reason}</dd></div> : null}
        {view.created ? <div><dt>Created</dt><dd>{view.created}</dd></div> : null}
      </dl>
      {open ? (
        <div className={styles.revokeForm}>
          <Field
            label="Reason for revoking"
            required
            help="Recorded in the audit log. While moderation enforces the post, it is evaluated again at once."
          >
            <Textarea id={reasonId} value={reason} rows={2} disabled={pending} onChange={(event) => onReasonChange(event.target.value)} />
          </Field>
          {error ? <Alert className={styles.flush} variant="error">{error}</Alert> : null}
          <div className={styles.inlineActions}>
            <Button size="compact" variant="secondary" disabled={pending} onClick={onCancel}>Cancel</Button>
            <Button size="compact" variant="danger" loading={pending} disabled={!reason.trim()} onClick={onRevoke}>
              Revoke exemption
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  )
}

// ModerationOverrideDialog is the Command Center's moderation override for
// one post: it reads the post's full moderation read model and offers what
// applies to it, a release for a provider outage, a release while
// moderation is off for the organization, or revoking an active exemption.
// Every action echoes the revision, hash and policy the operator was shown
// and records the operator's reason.
export function ModerationOverrideDialog({ open = true, orgSlug, postId, postTitle, organizationName, onClose }) {
  const queryClient = useQueryClient()
  const queryKey = postModerationQueryKey(orgSlug, postId)
  const postQuery = useQuery({
    queryKey,
    queryFn: () => postApi.get(orgSlug, postId),
    enabled: open && Boolean(orgSlug && postId),
  })
  const post = postQuery.data
  const moderation = post?.moderation || null

  const [reason, setReason] = useState('')
  const [chosenChecks, setChosenChecks] = useState(null)
  const [revokingId, setRevokingId] = useState(null)
  const [revokeReason, setRevokeReason] = useState('')
  const [releaseError, setReleaseError] = useState(null)
  const [revokeError, setRevokeError] = useState(null)
  const [staleNotice, setStaleNotice] = useState(null)

  const checks = chosenChecks ?? defaultOutageChecks(post, moderation)

  const finish = (message) => {
    toast.success(message)
    queryClient.invalidateQueries({ queryKey: ['feed-ops'] })
    queryClient.invalidateQueries({ queryKey })
    onClose?.()
  }

  // A stale refusal refreshes the post and asks the operator to look again.
  // A revocation of an exemption that is gone (404) refreshes it too, so the
  // list shows what is still active.
  const refuse = (error, setError, exemption) => {
    const refusal = classifyOverrideError(error, { exemption })
    if (refusal.kind === 'stale') {
      setStaleNotice(refusal.message)
      postQuery.refetch()
      queryClient.invalidateQueries({ queryKey: ['feed-ops'] })
      return
    }
    setError(refusal.message)
    if (exemption && error?.response?.status === 404) postQuery.refetch()
  }

  const clearMessages = () => {
    setReleaseError(null)
    setRevokeError(null)
    setStaleNotice(null)
  }

  const releaseMutation = useMutation({
    mutationFn: (payload) => moderationApi.overridePost(orgSlug, postId, payload),
    onMutate: clearMessages,
    onSuccess: (result) => finish(overrideSuccessMessage(result)),
    onError: (error) => refuse(error, setReleaseError),
  })

  const revokeMutation = useMutation({
    mutationFn: ({ exemption, reason: revokeWhy }) => moderationApi.revokeExemption(orgSlug, exemption.id, revokeWhy.trim()),
    onMutate: clearMessages,
    onSuccess: (result) => finish(revokeSuccessMessage(result)),
    onError: (error, { exemption }) => refuse(error, setRevokeError, exemption),
  })

  const busy = releaseMutation.isPending || revokeMutation.isPending
  const close = () => {
    if (busy) return
    onClose?.()
  }

  const options = overrideOptions(moderation)
  const known = versionsKnown(moderation)
  const view = moderationStatusView(moderation)
  const findings = uniqueFindingLabels(moderation?.findings || [])
  const exemptions = moderation?.exemptions || []
  const exemptionStillOpen = exemptions.some((exemption) => exemption.id === revokingId)
  const failureNotice = nonOutageFailureNotice(moderation)
  const outageNotice = outageReleaseNotice(moderation)
  const explanation = noOverrideExplanation(moderation)
  const notChecked = notCheckedLabel(moderation)
  const releasedBy = releasedByLabel(moderation)

  const releaseOutage = () => releaseMutation.mutate(outageReleasePayload(moderation, checks, reason))
  const releaseWhileOff = () => releaseMutation.mutate(offReleasePayload(moderation, reason))

  let footerAction = null
  if (moderation && options.outage) {
    footerAction = (
      <Button
        loading={releaseMutation.isPending}
        disabled={revokeMutation.isPending || !canSubmitOutageRelease(moderation, checks, reason)}
        onClick={releaseOutage}
      >
        Release for provider outage
      </Button>
    )
  } else if (moderation && options.offRelease) {
    footerAction = (
      <Button
        loading={releaseMutation.isPending}
        disabled={revokeMutation.isPending || !canSubmitOffRelease(moderation, reason)}
        onClick={releaseWhileOff}
      >
        Release while moderation is off
      </Button>
    )
  }

  let body
  if (postQuery.isLoading) {
    body = <StatePanel state="loading" compact title="Loading moderation state" />
  } else if (postQuery.isError) {
    body = (
      <StatePanel
        state="error"
        compact
        title="The post's moderation state could not be loaded"
        description={postQuery.error?.response?.data?.error || postQuery.error?.message || ''}
        onRetry={() => postQuery.refetch()}
      />
    )
  } else if (!moderation) {
    body = (
      <StatePanel
        state="empty"
        compact
        title="No moderation state"
        description="This post carries no moderation read model, so there is nothing to override."
      />
    )
  } else {
    body = (
      <>
        {staleNotice ? <Alert className={styles.flush} variant="warning">{staleNotice}</Alert> : null}
        {view ? (
          <Alert className={styles.flush} variant={TONE_VARIANTS[view.tone] || 'info'}>
            <strong>{view.title}</strong>
            <p className={styles.alertText}>{view.description}</p>
            {view.reason ? <p className={styles.alertText}>Reason: {view.reason}</p> : null}
          </Alert>
        ) : null}

        <dl className={styles.details} aria-label="Moderation state">
          <div><dt>Revision</dt><dd>{moderation.revision}</dd></div>
          <div><dt>Moderation</dt><dd>{moderationStateLabel(moderation)}</dd></div>
          {moderation.check_failed ? (
            <div>
              <dt>Failed check</dt>
              <dd>
                {checkFailureWords(moderation.failure_code) || 'No error code recorded'}
                {moderation.failure_code ? <> (<code>{moderation.failure_code}</code>)</> : null}
              </dd>
            </div>
          ) : null}
          {moderation.check_incomplete ? <div><dt>Incomplete check</dt><dd>{checksWords(moderation.incomplete_checks)}</dd></div> : null}
          {notChecked ? <div><dt>Not checked</dt><dd>{notChecked}</dd></div> : null}
          {releasedBy ? <div><dt>Released by</dt><dd>{releasedBy}</dd></div> : null}
          {moderation.pending_delete ? <div><dt>Deletion</dt><dd>Marked for deletion</dd></div> : null}
          {moderation.expired ? <div><dt>Expiry</dt><dd>Expired</dd></div> : null}
        </dl>

        <section className={styles.section} aria-labelledby="moderation-override-findings">
          <h3 id="moderation-override-findings" className={styles.sectionTitle}>Findings</h3>
          {findings.length ? (
            <ul className={styles.findings} aria-label="Moderation findings">
              {findings.map((label) => <li key={label}>{label}</li>)}
            </ul>
          ) : (
            <p className={styles.muted}>{moderation.findings_hidden ? 'Findings are hidden from this account.' : 'No findings on this revision.'}</p>
          )}
        </section>

        <section className={styles.section} aria-labelledby="moderation-override-exemptions">
          <h3 id="moderation-override-exemptions" className={styles.sectionTitle}>Active exemptions</h3>
          {exemptions.length ? (
            <ul className={styles.exemptions}>
              {exemptions.map((exemption) => (
                <ExemptionItem
                  key={exemption.id}
                  exemption={exemption}
                  open={revokingId === exemption.id}
                  reason={revokeReason}
                  error={revokingId === exemption.id ? revokeError : null}
                  pending={revokeMutation.isPending}
                  disabled={busy}
                  onOpen={() => {
                    setRevokingId(exemption.id)
                    setRevokeReason('')
                    setRevokeError(null)
                  }}
                  onCancel={() => {
                    setRevokingId(null)
                    setRevokeError(null)
                  }}
                  onReasonChange={setRevokeReason}
                  onRevoke={() => revokeMutation.mutate({ exemption, reason: revokeReason })}
                />
              ))}
            </ul>
          ) : <p className={styles.muted}>No active exemptions on this revision.</p>}
          {revokingId && !exemptionStillOpen && revokeError ? <Alert className={styles.flush} variant="error">{revokeError}</Alert> : null}
        </section>

        {options.outage ? (
          <section className={styles.section} aria-labelledby="moderation-override-outage">
            <h3 id="moderation-override-outage" className={styles.sectionTitle}>Release for provider outage</h3>
            <p className={styles.muted}>
              Records an exemption for the checks a provider outage left missing on this revision. Only a check that
              is missing with an outage on record is covered; the post is released once nothing else holds it.
            </p>
            {outageNotice ? <p className={styles.muted}>{outageNotice}</p> : null}
            <Fieldset label="Checks to cover" required help={outageChecksHelp(moderation)}>
              {OUTAGE_CHECKS.map((check) => (
                <Checkbox
                  key={check.value}
                  name="outage-checks"
                  value={check.value}
                  checked={checks.includes(check.value)}
                  disabled={busy}
                  onChange={(event) => setChosenChecks(toggleCheck(checks, check.value, event.target.checked))}
                  label={check.label}
                  description={check.description}
                />
              ))}
            </Fieldset>
          </section>
        ) : null}

        {options.offRelease ? (
          <section className={styles.section} aria-labelledby="moderation-override-off">
            <h3 id="moderation-override-off" className={styles.sectionTitle}>Release while moderation is off</h3>
            <p className={styles.muted}>{offReleaseDescription(moderation)}</p>
          </section>
        ) : null}

        {(options.outage || options.offRelease) ? (
          <>
            {!known ? <Alert className={styles.flush} variant="warning">{UNRESOLVED_POLICY_MESSAGE}</Alert> : null}
            <Field label="Reason" required help="Recorded with the exemption in the audit log.">
              <Textarea value={reason} rows={3} disabled={busy} onChange={(event) => setReason(event.target.value)} />
            </Field>
            {releaseError ? <Alert className={styles.flush} variant="error">{releaseError}</Alert> : null}
          </>
        ) : (
          failureNotice
            ? <Alert className={styles.flush} variant="warning">{failureNotice}</Alert>
            : <p className={styles.muted}>{explanation}</p>
        )}
      </>
    )
  }

  return (
    <Dialog
      open={open}
      title="Moderation override"
      description={overrideDialogDescription(postTitle, organizationName) || undefined}
      dismissable={!busy}
      onClose={close}
      footer={(
        <>
          <Button variant="secondary" disabled={busy} onClick={close}>Close</Button>
          {footerAction}
        </>
      )}
    >
      <div className={styles.body}>{body}</div>
    </Dialog>
  )
}

export default ModerationOverrideDialog
